import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export type Measurable = { uuid: string; width: number };

/**
 * Pure split: pack `items` (in order) into `available` px, accounting for the
 * `gap` px rendered between adjacent items. If anything spills, reserve
 * `moreWidth` px (plus one `gap`) for the More button and re-pack. When not
 * even one pill fits beside the More button, everything overflows — rendering
 * a pill that can't fit would only get it clipped mid-text against More.
 */
export function splitByWidth<T extends Measurable>(
  items: T[],
  available: number,
  moreWidth: number,
  gap = 0,
): { visible: T[]; overflow: T[] } {
  const fits = (budget: number) => {
    const visible: T[] = [];
    let used = 0;
    for (const item of items) {
      // Every item after the first is preceded by the flex `gap`, so it costs
      // `gap + width`. Omitting this let the rendered row exceed `available`.
      const cost = visible.length === 0 ? item.width : gap + item.width;
      if (used + cost <= budget) {
        visible.push(item);
        used += cost;
      } else break;
    }
    return visible;
  };

  const all = fits(available);
  if (all.length === items.length) return { visible: all, overflow: [] };

  // The More trigger sits after the pills, separated by one more `gap`.
  const visible = fits(Math.max(0, available - moreWidth - gap));
  const visibleIds = new Set(visible.map((i) => i.uuid));
  const overflow = items.filter((i) => !visibleIds.has(i.uuid));
  return { visible, overflow };
}

// Reserved px for the More dropdown trigger (label + padding). Keep in step
// with the rendered More button so the reserve-and-repack math stays honest.
const MORE_BUTTON_WIDTH = 84;

// Gap rendered between adjacent pills (and between the pills row and the More
// trigger). Keep in step with the `gap-2` (8px) on the pills row in
// AssetServicePills so the packing math matches the rendered layout.
const PILL_GAP = 8;

type Split<T> = { visible: T[]; overflow: T[] };

// Compare two splits by item identity + order. Lets `recompute` bail out of a
// no-op state update — essential because recompute can fire on every render
// (callers often pass a freshly-built `items` array), and an unconditional
// setSplit would re-render → recompute → setSplit forever (max update depth).
function sameSplit<T extends { uuid: string }>(a: Split<T>, b: Split<T>): boolean {
  if (a.visible.length !== b.visible.length) return false;
  if (a.overflow.length !== b.overflow.length) return false;
  return (
    a.visible.every((x, i) => x.uuid === b.visible[i].uuid) &&
    a.overflow.every((x, i) => x.uuid === b.overflow[i].uuid)
  );
}

/**
 * Returns the visible / overflow split for a set of service pills, wiring the
 * pure `splitByWidth` to a ResizeObserver on `containerRef`. Pills report their
 * rendered width through `registerWidth` (a ref callback); the split recomputes
 * once every pill has registered its width. Until then,
 * every item renders as visible so the unmeasured ones can register.
 *
 * Resilient to callers that pass a new `items` array each render: the observer
 * setup is keyed on the set of item uuids (not array identity), and `setSplit`
 * bails out when the result is unchanged — so an equivalent re-render can't
 * spin into an infinite update loop.
 */
export function useServicePillOverflow<T extends { uuid: string }>(items: T[]) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widthsRef = useRef<Record<string, number>>({});
  // Latest items, read by recompute. Kept current on every render so the split
  // reflects the freshest catalog without re-subscribing the observer.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const [split, setSplit] = useState<Split<T>>({ visible: items, overflow: [] });

  const recompute = useCallback(() => {
    const container = containerRef.current;
    // A hidden row (display:none on an ancestor) has no box at all. Its 0
    // width says nothing about room, and pill widths cached from an earlier
    // layout would otherwise split it as "nothing fits". Keep the last split;
    // the ResizeObserver re-runs this when the row is shown again.
    if (container && container.getClientRects().length === 0) return;
    const available = container?.clientWidth ?? 0;
    const measured = itemsRef.current.map((i) => ({
      ...i,
      width: widthsRef.current[i.uuid] ?? 0,
    }));
    const commit = (next: Split<T>) =>
      setSplit((prev) => (sameSplit(prev, next) ? prev : next));
    // A pill only reports its width once it renders, so any unmeasured item
    // (first mount, or a catalog that arrived / grew after mount) must be
    // rendered before a real split is possible. Keeping the previous split
    // instead would withhold those pills forever — they'd never render, so
    // never register, so never be split (DV-3193).
    if (measured.some((m) => m.width === 0)) {
      commit({ visible: itemsRef.current, overflow: [] });
      return;
    }
    // The row has a box and every pill is measured, so a 0 width is real: the
    // row is squeezed, not hidden. Splitting at 0 sends everything to the
    // Services trigger, whose width then floors the container. Bailing out
    // instead left every pill "visible" in a zero-width clip box with no
    // trigger (DV-3193 QA).
    commit(splitByWidth(measured, available, MORE_BUTTON_WIDTH, PILL_GAP));
  }, []);

  // Key on the uuid set, not the array reference, so a re-rendered-but-
  // equivalent `items` doesn't tear down and re-create the observer every render.
  const itemsKey = items.map((i) => i.uuid).join('|');
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    recompute();
    return () => ro.disconnect();
  }, [itemsKey, recompute]);

  const registerWidth = useCallback(
    (uuid: string, width: number) => {
      if (width && widthsRef.current[uuid] !== width) {
        widthsRef.current[uuid] = width;
        recompute();
      }
    },
    [recompute],
  );

  return { containerRef, registerWidth, ...split };
}
